import { useTranslation } from "react-i18next";
import { GoMakeDialog } from "@/components";
import { Stack, Typography } from "@mui/material";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { FONT_FAMILY } from "@/utils/font-family";

export const BATCH_ANALYZING_STORAGE_KEY = "gomake-batch-file-analyzing";
export const BATCH_CALCULATING_STORAGE_KEY = "gomake-batch-file-calculating";

export const markBatchAnalyzingPending = () => {
  if (typeof window !== "undefined") {
    sessionStorage.setItem(BATCH_ANALYZING_STORAGE_KEY, "1");
    window.dispatchEvent(new Event("gomake-batch-analyzing"));
  }
};

export const isBatchAnalyzingPending = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  return sessionStorage.getItem(BATCH_ANALYZING_STORAGE_KEY) === "1";
};

export const clearBatchAnalyzingPending = () => {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(BATCH_ANALYZING_STORAGE_KEY);
  }
};

/** @deprecated use isBatchAnalyzingPending + clearBatchAnalyzingPending */
export const consumeBatchAnalyzingPending = (): boolean => {
  const pending = isBatchAnalyzingPending();
  if (pending) {
    clearBatchAnalyzingPending();
  }
  return pending;
};

export const markBatchCalculatingActive = () => {
  if (typeof window !== "undefined") {
    sessionStorage.setItem(BATCH_CALCULATING_STORAGE_KEY, "1");
    window.dispatchEvent(new Event("gomake-batch-calculating"));
  }
};

export const isBatchCalculatingActive = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  return sessionStorage.getItem(BATCH_CALCULATING_STORAGE_KEY) === "1";
};

export const clearBatchCalculatingActive = () => {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(BATCH_CALCULATING_STORAGE_KEY);
  }
};

/** Close analyzing confirmation and switch to the calculating banner state. */
export const confirmBatchAnalyzingAndStartCalculating = () => {
  clearBatchAnalyzingPending();
  markBatchCalculatingActive();
};

interface BatchAnalyzingModalProps {
  open: boolean;
  onClose: () => void;
}

const BatchAnalyzingModal = ({ open, onClose }: BatchAnalyzingModalProps) => {
  const { t } = useTranslation();
  const { grayColor } = useGomakeTheme();

  return (
    <GoMakeDialog
      open={open}
      onClose={onClose}
      title={t("sales.quote.batchAnalyzingTitle")}
      showFooter={true}
      primaryButtonLabel={t("sales.quote.close")}
      onPrimaryAction={onClose}
      hideCancelButton={true}
      maxWidth="sm"
    >
      <Stack spacing={2} style={{ padding: "10px 0" }}>
        <Typography
          variant="body1"
          style={{
            color: grayColor(600),
            ...FONT_FAMILY.Lexend(400, 14),
            lineHeight: 1.6,
          }}
        >
          {t("sales.quote.batchAnalyzingDescription")}
        </Typography>
        <Typography
          variant="body2"
          style={{
            color: grayColor(500),
            ...FONT_FAMILY.Lexend(400, 13),
            lineHeight: 1.5,
          }}
        >
          {t("sales.quote.batchAnalyzingPatience")}
        </Typography>
      </Stack>
    </GoMakeDialog>
  );
};

export { BatchAnalyzingModal };
